import React, { useCallback, useEffect, useState } from 'react'
import { Form, Formik } from 'formik'
import { useTrans, useTealium, useIsMobile } from '@next-nx/hooks'
import { closest, numberNotation, priceNotation } from '@next-nx/utils/safe'
import { Button, FormikField } from '@next-nx/shared-ui/atoms'
import styles from './ConfiguratoreOfferta.module.sass'
import ArrowUp from '../../atoms/Icons/ArrowUp'
import { OffertaType, ScaglioneType } from '@next-nx/utils/types'
import { getPrezziContext, getTimestamp } from '@next-nx/utils'

interface Props {
  offerta: OffertaType
  autoDelMese?: boolean
  senzaAnticipo?: boolean
  onClick?: (e: any) => void
  className?: string
}

const ConfiguratoreOfferta = (props: Props) => {
  const {
    offerta,
    autoDelMese = false,
    senzaAnticipo: initialSenzaAnticipo = false,
    onClick = () => undefined,
    className = '',
  } = props

  const t = useTrans()
  const isMobile = useIsMobile()
  const { showIi: initialShowIi } = getPrezziContext(offerta.categoria.codice)
  const [swipeStart, setSwipeStart] = useState<number | null>(null)
  const [mobileOpen, setMobileOpen] = useState(false)

  const tipoUtenteOptions = [
    { id: '1', value: '1', label: t('Per Privati') },
    { id: '2', value: '2', label: t('Per Business/P.Iva') },
  ]

  const getUniqueKm = (scaglioni: ScaglioneType[]) => {
    return [...new Set(scaglioni.map((s) => s.km))]
  }

  const getChilometraggioOptions = () => {
    return getUniqueKm(offerta.scaglioni).map((x) => ({
      value: x,
      label: `${numberNotation(x, 0)} km`,
    }))
  }

  const getUniqueMesi = (scaglioni: ScaglioneType[], km: number) => {
    return [...new Set(scaglioni.filter((s) => s.km === km).map((s) => s.mesi))]
  }

  const getDurataOptions = (km: number) => {
    const uniqueMesi = getUniqueMesi(offerta.scaglioni, km)
    return uniqueMesi.map((x) => ({
      value: x,
      label: `${x} mesi`,
      tooltip:
        uniqueMesi.length > 1
          ? ''
          : t('Una sola opzione disponibile per il chilometraggio selezionato'),
    }))
  }

  const initialValues = {
    tipoUtente: initialShowIi ? tipoUtenteOptions[0].value : tipoUtenteOptions[1].value,
    anticipo: !initialSenzaAnticipo,
    chilometraggio: offerta.condizioniKm,
    durata: offerta.condizioniMesi,
    scaglioneSelected: offerta.scaglioni?.find(
      (s) => s.km === offerta.condizioniKm && s.mesi === offerta.condizioniMesi
    ),
  }

  const showIi = (props: any) => {
    return props.values.tipoUtente === '1' ? true : false
  }

  const { linkDataLayer } = useTealium()

  const handleTracking = () => {
    const action_timestamp = getTimestamp()

    linkDataLayer({
      virtual: 'action',
      action_name: 'richiesta_preventivo',
      action_tech: 'click',
      action_type: 'richiedi_preventivo',
      action_timestamp,
      action_effect: 'form_richiesta_preventivo_accesso',
    })
  }

  useEffect(() => {
    if (mobileOpen) document.body.classList.add('w-noscrollnav')
    else document.body.classList.remove('w-noscrollnav')
  }, [mobileOpen])

  return (
    <>
      {mobileOpen && <div className={styles.overlay}></div>}
      <div
        className={`${styles.root} ${mobileOpen ? styles['root--mobile-open'] : ''} ${className}`}
      >
        <Formik initialValues={initialValues} onSubmit={() => {}}>
          {(props) => (
            <>
              <div className={styles.main}>
                <button
                  role="button"
                  onClick={() => setMobileOpen(false)}
                  className={styles.closeTrigger}
                  onTouchStart={(e) => setSwipeStart(e.nativeEvent?.targetTouches[0]?.pageY)}
                  onTouchEnd={(e) => {
                    if (
                      swipeStart !== null &&
                      e.nativeEvent?.changedTouches[0]?.pageY > swipeStart + 20
                    ) {
                      // un po' di offset per verificare che swipino in giù
                      setMobileOpen(false)
                    }
                    setSwipeStart(null)
                  }}
                ></button>
                <div className={styles.openTriggerBox}>
                  <Button
                    label={t('Configura la tua auto')}
                    icon={<ArrowUp />}
                    iconPos="right"
                    variant="ghost"
                    onClick={() => setMobileOpen(true)}
                    className={styles.openTrigger}
                  />
                </div>
                {autoDelMese && <span className={styles.autoDelMese}>{t('Auto del mese')}</span>}
                <div className={styles.content}>
                  <div className={styles.innerContent}>
                    <p className={styles.title}>{t('Configura la tua auto')}</p>
                    <Form>
                      <FormikField
                        formik={props}
                        id="tipoUtente"
                        name="tipoUtente"
                        type="radio"
                        options={tipoUtenteOptions}
                        layout="full"
                        required
                        onChange={(e) => {
                          props.setFieldValue('tipoUtente', e.target.value)
                        }}
                      />
                      <FormikField
                        formik={props}
                        id="chilometraggio"
                        name="chilometraggio"
                        elementLabel={t('Chilometraggio annuo')}
                        type="slider"
                        options={getChilometraggioOptions()}
                        layout="full"
                        space="lg"
                        onChange={(e) => {
                          const newChilometraggio = e.target.value
                          props.setFieldValue('chilometraggio', newChilometraggio)

                          let newScaglione = offerta.scaglioni?.find(
                            (s) => s.km === newChilometraggio && s.mesi === props.values.durata
                          )
                          let newMesi = null
                          if (!newScaglione) {
                            const closestMesi = closest(
                              getUniqueMesi(offerta.scaglioni, newChilometraggio),
                              props.values.durata
                            )
                            const closestScaglione = offerta.scaglioni?.find(
                              (s) => s.km === newChilometraggio && s.mesi === closestMesi
                            )
                            newScaglione = closestScaglione
                            newMesi = closestMesi
                          }
                          props.setFieldValue('scaglioneSelected', newScaglione)
                          if (newMesi) {
                            props.setFieldValue('durata', newMesi)
                          }
                        }}
                      />
                      <FormikField
                        formik={props}
                        id="durata"
                        name="durata"
                        elementLabel={t('Durata noleggio')}
                        type="slider"
                        options={getDurataOptions(props.values.chilometraggio)}
                        layout="full"
                        space="lg"
                        onChange={(e) => {
                          const newDurata = e.target.value
                          props.setFieldValue('durata', newDurata)
                          props.setFieldValue(
                            'scaglioneSelected',
                            offerta.scaglioni?.find(
                              (s) => s.km === props.values.chilometraggio && s.mesi === newDurata
                            )
                          )
                        }}
                      />
                      {true && (
                        <div className={styles.anticipo}>
                          <div className={styles.anticipoField}>
                            <FormikField
                              formik={props}
                              id="anticipo"
                              name="anticipo"
                              elementLabel={
                                props.values.anticipo
                                  ? t('Con anticipo')
                                  : `${t('Senza anticipo')}*`
                              }
                              type="toggle"
                              layout="full"
                              space="none"
                            />
                          </div>
                          {props.values.anticipo && (
                            <div className={styles.anticipoValue}>
                              <span>
                                {showIi(props)
                                  ? priceNotation(offerta.anticipoIi)
                                  : priceNotation(offerta.anticipoIe)}
                              </span>{' '}
                              <span className={styles.anticipoIva}>
                                {showIi(props) ? t('iva inclusa') : t('iva esclusa')}
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                      {!props.values.anticipo && (
                        <div className={styles.senzaAnticipoDisclaimer}>
                          <div
                            dangerouslySetInnerHTML={{
                              __html: t(
                                '<strong>*Attenzione</strong>: L’offerta Senza anticipo è soggetta all’approvazione di UnipolRental S.p.A.'
                              ),
                            }}
                          />
                        </div>
                      )}
                      <div className={styles.bottom}>
                        <div className={styles.submit}>
                          <Button
                            label={t('Richiedi preventivo')}
                            labelMobile={t('Fai preventivo')}
                            onClick={() => {
                              onClick({
                                utentePrivato:
                                  props.values.tipoUtente === tipoUtenteOptions[0].value,
                              })
                            }}
                            handleTracking={handleTracking}
                            className={'form_richiesta_preventivo'.toHotjarFormat('{0}', isMobile)}
                          />
                        </div>
                      </div>
                    </Form>
                  </div>
                </div>
              </div>
              <div className={styles.preventivo}>
                <span className={styles.preventivoValue}>
                  {showIi(props)
                    ? priceNotation(
                        props.values.anticipo
                          ? props.values.scaglioneSelected?.canoneMaxirataIi
                          : props.values.scaglioneSelected?.canoneNoMaxirataIi
                      )
                    : priceNotation(
                        props.values.anticipo
                          ? props.values.scaglioneSelected?.canoneMaxirataIe
                          : props.values.scaglioneSelected?.canoneNoMaxirataIe
                      )}
                </span>
                <span className={styles.preventivoIva}>
                  {showIi(props) ? t('iva inclusa') : t('iva esclusa')}
                </span>
              </div>
            </>
          )}
        </Formik>
      </div>
    </>
  )
}

export default ConfiguratoreOfferta
